import { fmtDataIT } from "../lib/format";

import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Truck, Package, PackageCheck, Banknote, AlertTriangle, ChevronDown, ChevronRight, HandCoins, HelpCircle } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtEUR, fmtInt, fmtPct } from "../lib/format";
import { InfoTip } from "../components/InfoTip";
import { toast } from "sonner";

const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const labelMese = (m) => {
  const [a, mm] = (m || "").split("-");
  return mm ? `${MESI[parseInt(mm, 10) - 1]} ${a}` : m;
};

const FASCE = [
  { key: "0-5", label: "0–5 giorni", cls: "text-slate-700", bar: "bg-emerald-500" },
  { key: "6-10", label: "6–10 giorni", cls: "text-amber-700", bar: "bg-amber-500" },
  { key: "oltre-10", label: "Oltre 10 giorni — da sollecitare", cls: "text-red-700", bar: "bg-red-600" },
];

export default function CassaPage() {
  const { storeId, from, to, isMaster } = useOutletContext();
  const [data, setData] = useState(null);
  const [aperta, setAperta] = useState(null);

  useEffect(() => {
    setData(null);
    setAperta(null);
    api.get(`/cassa-contrassegni?store_id=${storeId}&from=${from}&to=${to}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [storeId, from, to]);

  if (!data) return (
    <div data-testid="cassa-skeleton" className="space-y-4">
      <div className="skeleton h-8 w-72" />
      <div className="skeleton h-24" />
      <div className="grid md:grid-cols-4 gap-3">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-28" />)}</div>
      <div className="skeleton h-48" />
    </div>
  );
  const b = data.blocchi;
  const prev = data.previsione;

  const STEP_STYLES = [
  { wrap: "bg-slate-50 border-slate-300", icon: "text-slate-500", val: "text-slate-800" },
  { wrap: "bg-amber-50 border-amber-200", icon: "text-amber-500", val: "text-amber-800" },
  { wrap: "bg-amber-100/60 border-amber-300", icon: "text-amber-600", val: "text-amber-900" },
  { wrap: "bg-emerald-50 border-emerald-200", icon: "text-emerald-600", val: "text-emerald-800" },
];

const Blocco = ({ icon: Icon, titolo, sub, blocco, testid, step }) => (
  <div data-testid={testid} className={`border rounded-xl p-5 fade-in ${STEP_STYLES[step].wrap}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`w-5 h-5 ${STEP_STYLES[step].icon}`} />
      <span className="text-sm font-semibold text-slate-600">{titolo}</span>
    </div>
    <div className={`text-3xl font-extrabold font-mono tracking-tight text-right tnum ${STEP_STYLES[step].val}`}>{fmtEUR(blocco.importo)}</div>
    <div className="text-sm text-slate-500 text-right mt-1">{fmtInt(blocco.ordini)} ordini · {sub}</div>
  </div>
);

  return (
    <div data-testid="cassa-page" className="space-y-6">
      <div className="flex items-baseline gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Cassa contrassegni</h1>
        <span className="text-sm text-slate-500 font-mono">{fmtDataIT(data.periodo.from)} → {fmtDataIT(data.periodo.to)}</span>
        {data.consolidato && <span className="text-xs bg-slate-900 text-white rounded px-2 py-0.5">Consolidato</span>}
      </div>

      {data.sempre && (
        <div data-testid="cassa-sempre-block" className="bg-amber-50/70 border border-amber-200 rounded-xl p-5 fade-in space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-1.5">
                <HandCoins className="w-4 h-4" />
                Da incassare complessivo — tutti i mesi
                <InfoTip text="Tutti gli ordini in contrassegno ancora aperti e non incassati, di qualunque mese: ignora il filtro periodo in alto" testid="cassa-sempre-tip" />
              </div>
              <div data-testid="cassa-sempre-totale" className="text-3xl font-extrabold font-mono tracking-tight text-amber-800 tnum mt-1">{fmtEUR(data.sempre.totale)}</div>
              <div className="text-xs text-amber-700 mt-1">{fmtInt(data.sempre.ordini)} ordini aperti — non cambia con il filtro periodo</div>
            </div>
            <div className="text-sm text-slate-600 space-y-1 min-w-[260px]">
              <div className="flex justify-between gap-6"><span>Ancora in transito</span><span className="font-mono tnum font-semibold" data-testid="cassa-sempre-transito">{fmtEUR(data.sempre.per_stato.in_transito.importo)} <span className="text-xs text-slate-400">({fmtInt(data.sempre.per_stato.in_transito.ordini)})</span></span></div>
              <div className="flex justify-between gap-6"><span>Consegnati, non ancora incassati</span><span className="font-mono tnum font-semibold" data-testid="cassa-sempre-consegnati">{fmtEUR(data.sempre.per_stato.consegnato_non_incassato.importo)} <span className="text-xs text-slate-400">({fmtInt(data.sempre.per_stato.consegnato_non_incassato.ordini)})</span></span></div>
            </div>
          </div>
          {data.sempre.per_mese.length > 0 && (
            <div data-testid="cassa-sempre-mesi" className="flex flex-wrap gap-2">
              {data.sempre.per_mese.map((m) => (
                <span key={m.mese} data-testid={`cassa-sempre-mese-${m.mese}`} className="text-xs font-mono bg-white border border-amber-200 text-amber-800 rounded-full px-3 py-1">
                  {labelMese(m.mese)}: <b>{fmtEUR(m.importo)}</b> · {fmtInt(m.ordini)} ordini
                </span>
              ))}
            </div>
          )}
          {data.sempre.oltre_10gg.length > 0 ? (
            <div data-testid="cassa-sempre-oltre10" className="bg-red-50 border border-red-200 rounded-md px-3 py-2">
              <div className="text-xs font-semibold text-red-700 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Consegnati da oltre 10 giorni senza accredito: {fmtInt(data.sempre.oltre_10gg.length)} ordini · {fmtEUR(data.sempre.oltre_10gg.reduce((s, o) => s + o.importo, 0))} — da sollecitare al corriere
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                {data.sempre.oltre_10gg.slice(0, 8).map((o) => (
                  <span key={o.ordine_id} className="text-[11px] font-mono text-red-700">{o.id_ordine_esterno} ({o.giorni} gg · {fmtEUR(o.importo)})</span>
                ))}
                {data.sempre.oltre_10gg.length > 8 && <span className="text-[11px] text-red-500">+ altri {data.sempre.oltre_10gg.length - 8}</span>}
              </div>
            </div>
          ) : (
            <div data-testid="cassa-sempre-oltre10" className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-xs font-semibold text-emerald-700">
              Nessun ordine consegnato da oltre 10 giorni senza accredito.
            </div>
          )}
        </div>
      )}

      <div data-testid="cassa-totale-banner" className="bg-slate-900 text-white rounded-lg px-5 py-4 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">Totale contrassegni ancora da incassare — tutti i periodi</div>
          <div className="text-3xl font-extrabold font-mono tracking-tight tnum">{fmtEUR(data.totale_da_incassare)}</div>
        </div>
        <div className="text-sm text-slate-300">
          di cui <span className="font-mono font-bold text-emerald-300">{fmtEUR(b.consegnato_non_incassato.importo)}</span> già consegnati (quasi certi)
          {" "}e <span className="font-mono font-bold">{fmtEUR(b.non_partiti.importo + b.in_viaggio.importo + b.non_determinabile.importo)}</span> non ancora consegnati
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] gap-2 items-stretch">
        <Blocco icon={Package} titolo="1 · Ordini presi, non ancora partiti" sub="etichetta creata, pacco in magazzino" blocco={b.non_partiti} testid="blocco-non-partiti" step={0} />
        <div className="hidden md:flex items-center justify-center text-slate-300"><ChevronRight className="w-6 h-6" /></div>
        <Blocco icon={Truck} titolo="2 · Spediti, in viaggio" sub="il corriere ha il pacco (presa in carico)" blocco={b.in_viaggio} testid="blocco-in-viaggio" step={1} />
        <div className="hidden md:flex items-center justify-center text-slate-300"><ChevronRight className="w-6 h-6" /></div>
        <Blocco icon={PackageCheck} titolo="3 · Consegnati, soldi non ancora arrivati" sub="il corriere ha i soldi" blocco={b.consegnato_non_incassato} testid="blocco-consegnato-non-incassato" step={2} />
        <div className="hidden md:flex items-center justify-center text-slate-300"><ChevronRight className="w-6 h-6" /></div>
        <Blocco icon={Banknote} titolo="4 · Consegnati e incassati" sub="periodo selezionato — denaro accreditato" blocco={b.incassato} testid="blocco-incassato" step={3} />
      </div>

      <div data-testid="blocco-non-determinabile" className="border border-slate-300 border-dashed bg-slate-50 rounded-xl p-5 flex flex-wrap items-center justify-between gap-3 fade-in">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Da verificare — non sappiamo se sono partiti</span>
        </div>
        <div className="text-right">
          <div className="text-3xl font-extrabold font-mono tracking-tight tnum text-slate-800">{fmtEUR(b.non_determinabile.importo)}</div>
          <div className="text-sm text-slate-500">{fmtInt(b.non_determinabile.ordini)} ordini · nessuna spedizione attiva nell'hub: spediti fuori hub o mai spediti</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-slate-800 font-heading mb-3">Anzianità — consegnati non ancora incassati</h2>
        <div className="space-y-2">
          {FASCE.map((f) => {
            const fa = data.aging[f.key];
            const open = aperta === f.key;
            const critico = f.key === "oltre-10" && fa.ordini > 0;
            return (
              <div key={f.key} data-testid={`aging-${f.key}`}
                className={`border rounded-md ${critico ? "border-red-300 bg-red-50/50" : "border-slate-200"}`}>
                <button data-testid={`aging-toggle-${f.key}`} onClick={() => setAperta(open ? null : f.key)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left">
                  {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  {critico && <AlertTriangle className="w-4 h-4 text-red-600" />}
                  <span className={`text-sm font-semibold ${f.cls}`}>{f.label}</span>
                  <span className="ml-auto font-mono text-sm tnum">{fmtInt(fa.ordini)} ordini</span>
                  <span className={`font-mono font-bold tnum ${critico ? "text-red-700" : "text-slate-900"}`}>{fmtEUR(fa.importo)}</span>
                </button>
                {open && (
                  <div className="overflow-x-auto">
                  <table className="w-full text-sm border-t border-slate-100 min-w-[520px]">
                    <thead><tr className="text-left text-xs uppercase text-slate-500">
                      <th className="px-4 py-1.5">Ordine</th><th>Cliente</th>{data.consolidato && <th>Store</th>}
                      <th>Data consegna</th><th className="text-right">Giorni</th><th className="text-right pr-4">Importo</th>
                    </tr></thead>
                    <tbody>
                      {fa.lista.map((o) => (
                        <tr key={o.ordine_id} data-testid={`aging-row-${o.id_ordine_esterno}`} className="border-t border-slate-50">
                          <td className="px-4 py-1.5 font-mono text-xs">{o.id_ordine_esterno}</td>
                          <td className="text-xs">{o.cliente || "—"}</td>
                          {data.consolidato && <td className="text-xs text-slate-500">{o.store_nome}</td>}
                          <td className="font-mono text-xs">{fmtDataIT(o.data_consegna)}</td>
                          <td className={`font-mono text-xs text-right ${o.giorni > 10 ? "text-red-700 font-bold" : ""}`}>{o.giorni} gg</td>
                          <td className="font-mono text-xs text-right pr-4 tnum">{fmtEUR(o.importo)}</td>
                        </tr>
                      ))}
                      {fa.lista.length === 0 && <tr><td colSpan={6} className="px-4 py-3 text-center text-slate-400 text-xs">Nessun ordine in questa fascia</td></tr>}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="previsione-card">
          <h2 className="text-lg font-semibold text-slate-800 font-heading mb-3">Previsione di rientro</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Consegnati non ancora incassati (quasi certi)</span><span className="font-mono tnum">{fmtEUR(prev.consegnati_non_incassati)}</span></div>
            <div className="flex justify-between">
              <span className="text-slate-600">Non ancora consegnati × tasso di incasso ({fmtPct(prev.tasso_usato)})</span>
              <span className="font-mono tnum">{fmtEUR(prev.in_transito_stimato)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
              <span className="font-semibold text-slate-900">Totale previsto in entrata</span>
              <span data-testid="previsione-totale" className="font-mono font-bold text-slate-900 tnum">{fmtEUR(prev.totale_previsto)}</span>
            </div>
          </div>
          {!prev.tasso_da_storico && (
            <div data-testid="tasso-manuale-avviso" className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
              Storico insufficiente ({prev.campione} casi conclusi): il tasso usato è quello impostato a mano nella configurazione store.
            </div>
          )}
          {prev.tasso_da_storico && (
            <div className="mt-3 text-xs text-slate-400">Tasso calcolato sullo storico dello store ({prev.campione} casi conclusi).</div>
          )}
          <p className="text-xs text-slate-400 mt-2">{prev.note_tempi}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="prepagati-card">
          <h2 className="text-lg font-semibold text-slate-800 font-heading mb-3">Prepagati (fuori dal flusso COD)</h2>
          <div className="flex justify-between text-sm"><span className="text-slate-600">Ordini prepagati (carta, PayPal, wallet, BNPL)</span><span className="font-mono tnum">{fmtInt(data.prepagati.ordini)}</span></div>
          <div className="flex justify-between text-sm mt-2"><span className="text-slate-600">Già incassati all'ordine</span><span className="font-mono font-bold tnum">{fmtEUR(data.prepagati.incassato)}</span></div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>Quota del fatturato via contrassegno</span>
              <span className="font-mono">{fmtPct(data.prepagati.quota_contrassegno_sul_fatturato)}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${(data.prepagati.quota_contrassegno_sul_fatturato * 100).toFixed(1)}%` }} />
            </div>
          </div>
        </div>
      </div>

      {data.consolidato && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table data-testid="cassa-per-store-table" className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100/80 text-left">
                {["Store", "Non partiti", "In viaggio", "Da verificare", "Consegnato non incassato", "Incassato", "Tasso storico", "Prev. su aperti"].map((h) => (
                  <th key={h} className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 ${h === "Store" ? "" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.per_store.map((s) => (
                <tr key={s.store_id} data-testid={`cassa-store-${s.store_nome}`} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium text-slate-700">{s.store_nome}</td>
                  <td className="px-3 py-2 font-mono text-right tnum">{fmtEUR(s.blocchi.non_partiti.importo)}</td>
                  <td className="px-3 py-2 font-mono text-right tnum">{fmtEUR(s.blocchi.in_viaggio.importo)}</td>
                  <td className="px-3 py-2 font-mono text-right tnum text-slate-400">{fmtEUR(s.blocchi.non_determinabile.importo)}</td>
                  <td className="px-3 py-2 font-mono text-right tnum font-semibold">{fmtEUR(s.blocchi.consegnato_non_incassato.importo)}</td>
                  <td className="px-3 py-2 font-mono text-right tnum">{fmtEUR(s.blocchi.incassato.importo)}</td>
                  <td className="px-3 py-2 font-mono text-right tnum">
                    {fmtPct(s.tasso_incasso_storico)}
                    {!s.tasso_da_storico && <span className="ml-1 text-[10px] text-amber-600">(manuale)</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-right tnum">{fmtEUR(s.previsione_transito)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
